import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-use';
import {
  AbsoluteCenter,
  Alert,
  AlertIcon,
  AlertTitle,
  Card,
  Container,
  GridItem,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import * as yup from 'yup';
import { Buffer } from 'buffer';
import { CREATE_CART_QUERY } from '@rsc/contexts/cartQueries';
import { CartContext } from '@rsc/contexts';
import { Button } from '@rsc/ui-components/shadcn/Button';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import { DeptGrid } from '../../../design-system';
import { FETCH_PRODUCT_UPDATE_QUERY, makeShopifyRequest } from '../../../utils';

function base64(value) {
  return Buffer.from(value).toString('base64');
}

// TODO: which fields are required?
const getIotUserSchema = emailMessage =>
  yup.object({
    email: yup.string().trim().email(emailMessage).required(),
    iotUserId: yup.string(),
    iotGroupId: yup.string(),
    deviceId: yup.string(),
    collarId: yup.string(),
    thingName: yup.string(),
    petName: yup.string().required(),
    petId: yup.string(),
    customerAccessToken: yup.string(),
    customerAccessTokenExpiresAt: yup.string(),
  });

function iotUserFromToken(token, emailMessage) {
  try {
    const decodedString = decodeURIComponent(atob(token));

    // Remove non-printable characters
    const cleanString = decodedString.replace(/[^\x20-\x7E]/g, '');

    const userInput = JSON.parse(cleanString);

    const iotUserSchema = getIotUserSchema(emailMessage);
    return iotUserSchema.validateSync(userInput);
  } catch (err) {
    console.error(err);
  }
  return null;
}

async function fetchSellingPlan(client, { productId, variantId }) {
  const res = await makeShopifyRequest(
    FETCH_PRODUCT_UPDATE_QUERY,
    {
      id: productId,
    },
    client
  );
  const found = res.product.variants.nodes.find(v => v.id === variantId);
  return found.sellingPlanAllocations.nodes[0].sellingPlan;
}

async function createCart(
  client,
  { attributes, buyerIdentity, discountCodes, lines, metafields, note }
) {
  return client.request(CREATE_CART_QUERY, {
    input: {
      attributes: [
        ...attributes,
        ...(process.env.GATSBY_RSC_ENV
          ? [
              {
                key: 'rscEnv',
                value: process.env.GATSBY_RSC_ENV,
              },
            ]
          : []),
      ],
      buyerIdentity,
      discountCodes,
      lines,
      metafields,
      note,
    },
  });
}

export default function SubscriptionGrid({ block }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { tt } = useUiComponentsInternationalContext();

  const { shopifyClient } = useContext(CartContext);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  async function onPlanSelect(plan) {
    setIsLoading(true);
    try {
      const iotUser = iotUserFromToken(
        searchParams.get('token'),
        tt(s => s.blocksDeptComponents.emailValidationMessage)
      );
      const sellingPlan = await fetchSellingPlan(shopifyClient, {
        productId: plan.product.shopifyProduct.shopifyId,
        variantId: plan.productVariant.store.gid,
      });

      const checkoutVisibleLineItem = {
        attributes: [{ key: 'Pet Name', value: iotUser.petName }],
        merchandiseId: plan.productVariant.store.gid,
        quantity: 1,
        sellingPlanId: base64(sellingPlan.id),
      };

      const orderAttributes = [
        [tt(s => s.blocksDeptComponents.petNameLabel), 'petName'],
        [tt(s => s.blocksDeptComponents.emailLabel), 'email'],
        [tt(s => s.blocksDeptComponents.iotUserIdLabel), 'iotUserId'],
        [tt(s => s.blocksDeptComponents.iotGroupIdLabel), 'iotGroupId'],
        [tt(s => s.blocksDeptComponents.deviceIdLabel), 'deviceId'],
        [tt(s => s.blocksDeptComponents.collarIdLabel), 'collarId'],
        [tt(s => s.blocksDeptComponents.thingNameLabel), 'thingName'],
        [tt(s => s.blocksDeptComponents.petIdLabel), 'petId'],
      ].reduce(
        (acc, [key, userProperty]) =>
          iotUser[userProperty]
            ? [...acc, { key, value: iotUser[userProperty] }]
            : acc,
        []
      );

      const cartInput = {
        attributes: orderAttributes,
        buyerIdentity: {
          countryCode: 'US', // ! for international pricing
          customerAccessToken: iotUser.customerAccessToken || null, // ! will log user in when visiting checkout
          email: iotUser.email || null, // ! auto-fills email on checkout
        },
        discountCodes: [],
        lines: [checkoutVisibleLineItem], // ! need at least 1 item to create a checkout else you will get redirected to shopify store homepage
        metafields: [],
        note: '',
      };

      if (searchParams.get('rscdebug')) {
        console.log('iotUser: ', iotUser, 'cartInput: ', cartInput);
      }

      const cart = await createCart(shopifyClient, cartInput);
      window.location = cart?.cartCreate.cart.checkoutUrl;
    } catch (err) {
      console.error('Failed plan select', err);
      setErrors([tt(s => s.blocksDeptComponents.failPlanSelectMessage)]);
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return (
      <Container centerContent minHeight="200" position="relative">
        <AbsoluteCenter>
          <Spinner pos="relative" h="4" w="4" />
        </AbsoluteCenter>
      </Container>
    );
  }

  return (
    <Container centerContent minHeight="200" position="relative">
      <VStack align="flex-start" w="full" spacing={4}>
        {errors?.map(err => (
          <Alert key={err} status="error">
            <AlertIcon />
            <AlertTitle>{err}</AlertTitle>
          </Alert>
        ))}
        {block?.heading && (
          <Text as="h1" textStyle="h2" fontWeight="black" mb={4}>
            {block?.heading}
          </Text>
        )}
        <DeptGrid
          templateColumns={{
            base: 'repeat(1, 1fr)',
            md: `repeat(${block.plans?.length}, 1fr)`,
          }}
          alignSelf="stretch"
        >
          {block?.plans?.map(plan => (
            <GridItem
              key={plan?.description || plan?.price}
              w="full"
              bg="neutral.light"
              p={4}
              as={Card}
              borderRadius="lg"
              border="1px solid"
              borderColor="stroke.light"
            >
              <VStack justifyContent="center" h="full" minHeight="200">
                <VStack justifyContent="center" h="full">
                  {plan?.oldPrice && (
                    <Text
                      fontSize="lg"
                      as="s"
                      fontWeight="600"
                      lineHeight="115%"
                      letterSpacing="-0.48px"
                    >
                      {plan.oldPrice}
                    </Text>
                  )}
                  {plan?.price && (
                    <Text
                      fontSize="xl"
                      color="primary.900"
                      fontWeight="600"
                      lineHeight="115%"
                      letterSpacing="-0.64px"
                    >
                      {plan.price}
                    </Text>
                  )}
                  {plan?.description && (
                    <Text fontSize="xs" fontWeight="600" lineHeight="120%">
                      {plan.description?.trim()}
                    </Text>
                  )}
                  {plan?.hasFreeTrial && (
                    <Text fontSize="sm" fontWeight="light" lineHeight="150%">
                      {plan?.freeTrial?.trim()}
                    </Text>
                  )}
                </VStack>
                <Button
                  type="button"
                  onClick={() => onPlanSelect(plan)}
                  aria-label={tt(
                    s => s.blocksDeptComponents.subscriptionPlanAriaLabel
                  )}
                  disabled={isLoading}
                  variant="cta"
                  className="text-base font-semibold w-full"
                >
                  {plan?.ctaLabel ||
                    tt(s => s.blocksDeptComponents.selectPlanButton)}
                </Button>
              </VStack>
            </GridItem>
          ))}
        </DeptGrid>
      </VStack>
    </Container>
  );
}

SubscriptionGrid.propTypes = {
  block: PropTypes.shape({
    heading: PropTypes.string,
    plans: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  }).isRequired,
};
